import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { articleTranslationService } from '@/lib/services/articleTranslationService';
import { toast } from 'sonner';

interface Article {
  id: string;
  title: { ar: string; en: string; };
  excerpt: { ar: string; en: string; };
  content: { ar: string; en: string; };
  author: { ar: string; en: string; };
  category: { ar: string; en: string; };
  image_url: string;
  read_time: number;
  created_at: string;
}

interface AddArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (article: Omit<Article, 'id' | 'created_at'>) => Promise<void>;
}

export function AddArticleModal({ isOpen, onClose, onSave }: AddArticleModalProps) {
  const [newArticle, setNewArticle] = useState({
    title: { ar: '', en: '' },
    excerpt: { ar: '', en: '' },
    content: { ar: '', en: '' },
    author: { ar: '', en: '' },
    category: { ar: '', en: '' },
    image_url: '',
    read_time: 5,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(newArticle);
      onClose();
      // Reset form
      setNewArticle({
        title: { ar: '', en: '' },
        excerpt: { ar: '', en: '' },
        content: { ar: '', en: '' },
        author: { ar: '', en: '' },
        category: { ar: '', en: '' },
        image_url: '',
        read_time: 5,
      });
    } catch (error) {
      console.error('Error saving article:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTranslateToEnglish = async () => {
    const { title, excerpt, content } = newArticle;
    
    if (!title.ar || !excerpt.ar || !content.ar) {
      toast.error('الرجاء ملء المحتوى العربي أولاً');
      return;
    }

    setIsTranslating(true);
    toast.info('🤖 جاري الترجمة إلى الإنجليزية...');

    try {
      const result = await articleTranslationService.translateToEnglish(
        title.ar,
        excerpt.ar,
        content.ar
      );

      setNewArticle({
        ...newArticle,
        title: { ar: title.ar, en: result.titleEn },
        excerpt: { ar: excerpt.ar, en: result.excerptEn },
        content: { ar: content.ar, en: result.contentEn },
      });

      toast.success('✅ تمت الترجمة بنجاح!');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('❌ فشلت الترجمة. خدمة الذكاء الاصطناعي غير متاحة — اطلب من المسؤول التحقق من مفتاح الخادم.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTranslateToArabic = async () => {
    const { title, excerpt, content } = newArticle;
    
    if (!title.en || !excerpt.en || !content.en) {
      toast.error('Please fill in the English content first');
      return;
    }

    setIsTranslating(true);
    toast.info('🤖 Translating to Arabic...');

    try {
      const result = await articleTranslationService.translateToArabic(
        title.en,
        excerpt.en,
        content.en
      );

      setNewArticle({
        ...newArticle,
        title: { ar: result.titleAr, en: title.en },
        excerpt: { ar: result.excerptAr, en: excerpt.en },
        content: { ar: result.contentAr, en: content.en },
      });

      toast.success('✅ Translation completed successfully!');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('❌ Translation failed. The AI service is unavailable — ask an administrator to check the server key.');
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>إضافة مقال جديد</span>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleTranslateToEnglish}
                disabled={isTranslating || !newArticle.title.ar}
                className="gap-2"
              >
                {isTranslating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                ترجمة للإنجليزية
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleTranslateToArabic}
                disabled={isTranslating || !newArticle.title.en}
                className="gap-2"
              >
                {isTranslating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Translate to Arabic
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">العنوان / Title</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Input
                  id="title-ar"
                  type="text"
                  value={newArticle.title.ar}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    title: { ...newArticle.title, ar: e.target.value }
                  })}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                <Input
                  id="title-en"
                  type="text"
                  value={newArticle.title.en}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    title: { ...newArticle.title, en: e.target.value }
                  })}
                  placeholder="English title"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Excerpt - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">المقدمة / Excerpt</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="excerpt-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Textarea
                  id="excerpt-ar"
                  value={newArticle.excerpt.ar}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    excerpt: { ...newArticle.excerpt, ar: e.target.value }
                  })}
                  rows={3}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt-en" className="text-sm text-muted-foreground">English</Label>
                <Textarea
                  id="excerpt-en"
                  value={newArticle.excerpt.en}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    excerpt: { ...newArticle.excerpt, en: e.target.value }
                  })}
                  rows={3}
                  placeholder="English excerpt"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Content - Side by Side */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">المحتوى / Content</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="content-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Textarea
                  id="content-ar"
                  value={newArticle.content.ar}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    content: { ...newArticle.content, ar: e.target.value }
                  })}
                  rows={8}
                  required
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content-en" className="text-sm text-muted-foreground">English</Label>
                <Textarea
                  id="content-en"
                  value={newArticle.content.en}
                  onChange={(e) => setNewArticle({
                    ...newArticle,
                    content: { ...newArticle.content, en: e.target.value }
                  })}
                  rows={8}
                  placeholder="English content"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* Author and Category fields - Bilingual */}
          <div className="space-y-4">
            {/* Author */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">الكاتب / Author</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="author-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="author-ar"
                    value={newArticle.author.ar}
                    onChange={(e) => setNewArticle(prev => ({
                      ...prev,
                      author: { ...prev.author, ar: e.target.value }
                    }))}
                    placeholder="مثال: أحمد محمد"
                    required
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="author-en"
                    value={newArticle.author.en}
                    onChange={(e) => setNewArticle(prev => ({
                      ...prev,
                      author: { ...prev.author, en: e.target.value }
                    }))}
                    placeholder="Example: Ahmad Mohammad"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">التصنيف / Category</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="category-ar"
                    value={newArticle.category.ar}
                    onChange={(e) => setNewArticle(prev => ({
                      ...prev,
                      category: { ...prev.category, ar: e.target.value }
                    }))}
                    placeholder="مثال: صحة"
                    required
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="category-en"
                    value={newArticle.category.en}
                    onChange={(e) => setNewArticle(prev => ({
                      ...prev,
                      category: { ...prev.category, en: e.target.value }
                    }))}
                    placeholder="Example: Health"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Other fields */}
          <div className="grid grid-cols-2 gap-4" dir="rtl">
            <div>
              <Label htmlFor="image_url">رابط الصورة</Label>
              <Input
                id="image_url"
                type="url"
                value={newArticle.image_url}
                onChange={(e) => setNewArticle({ ...newArticle, image_url: e.target.value })}
                required
                dir="ltr"
              />
            </div>
            <div>
              <Label htmlFor="read_time">وقت القراءة (بالدقائق)</Label>
              <Input
                id="read_time"
                type="number"
                value={newArticle.read_time}
                onChange={(e) => setNewArticle({ ...newArticle, read_time: parseInt(e.target.value) })}
                min="1"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving || isTranslating}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              disabled={isSaving || isTranslating}
            >
              {isSaving ? 'جاري الحفظ...' : 'إضافة المقال'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
